/**
 * Standardized Stat Card Components
 * Based on warranty page patterns
 * 
 * Variants:
 * - Default: Standard stat card with icon
 * - Critical: Red background for urgent items (bg-red-50 border-red-200)
 * - Warning: Yellow background for warnings (bg-yellow-50 border-yellow-200)
 * - Success: Green background for success (bg-green-50 border-green-200)
 */

import React, { ReactNode } from 'react';
import { Card } from '@/components/ui/card';
import { LucideIcon } from 'lucide-react';

interface StatCardProps {
  label: string;
  value: string | number;
  icon?: LucideIcon;
  variant?: 'default' | 'critical' | 'warning' | 'success';
  subtitle?: string;
  className?: string;
}

export function StatCard({ 
  label, 
  value, 
  icon: Icon, 
  variant = 'default',
  subtitle,
  className = ''
}: StatCardProps) {
  const variantStyles = {
    default: 'bg-white',
    critical: 'bg-red-50 border-red-200',
    warning: 'bg-yellow-50 border-yellow-200',
    success: 'bg-green-50 border-green-200'
  };

  const labelColors = {
    default: 'text-gray-600',
    critical: 'text-red-700 font-medium',
    warning: 'text-yellow-700 font-medium',
    success: 'text-green-700 font-medium'
  };

  const valueColors = {
    default: 'text-gray-900',
    critical: 'text-red-600',
    warning: 'text-yellow-600',
    success: 'text-green-600'
  };

  const iconColors = {
    default: 'text-gray-400',
    critical: 'text-red-600',
    warning: 'text-yellow-600',
    success: 'text-green-600'
  };

  return (
    <Card className={`p-4 ${variantStyles[variant]} ${className}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className={`text-sm ${labelColors[variant]}`}>{label}</p>
          <p className={`text-2xl font-bold ${valueColors[variant]}`}>{value}</p>
          {subtitle && (
            <p className="text-xs text-gray-600 mt-1">{subtitle}</p>
          )}
        </div>
        {Icon && (
          <Icon className={`h-8 w-8 ${iconColors[variant]}`} />
        )}
      </div>
    </Card>
  );
}

interface AlertCardProps {
  title: string;
  icon?: LucideIcon;
  variant: 'critical' | 'warning' | 'success';
  children: ReactNode;
  count?: number;
  className?: string;
}

export function AlertCard({
  title,
  icon: Icon,
  variant,
  children,
  count,
  className = ''
}: AlertCardProps) {
  const variantStyles = {
    critical: 'border-red-200 bg-red-50',
    warning: 'border-yellow-200 bg-yellow-50',
    success: 'border-green-200 bg-green-50'
  };

  const titleColors = {
    critical: 'text-red-900',
    warning: 'text-yellow-900',
    success: 'text-green-900'
  };

  const iconColors = {
    critical: 'text-red-600',
    warning: 'text-yellow-600',
    success: 'text-green-600'
  };

  const countColors = {
    critical: 'text-red-700',
    warning: 'text-yellow-700',
    success: 'text-green-700'
  };

  return (
    <Card className={`p-6 ${variantStyles[variant]} ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {Icon && <Icon className={`h-5 w-5 ${iconColors[variant]}`} />}
          <h2 className={`text-xl font-semibold ${titleColors[variant]}`}>
            {title}
          </h2>
        </div>
        {count !== undefined && (
          <span className={`text-sm ${countColors[variant]}`}>
            {count} {count === 1 ? 'item' : 'items'}
          </span>
        )}
      </div>
      {children}
    </Card>
  );
}
